/**
 * Calendar boundaries in Asia/Kolkata (IST), matching server `startOfDayIST` / `endOfDayIST`.
 * Preset ranges use IST so "This month" / "This week" align with how the API interprets `yyyy-MM-dd`.
 */

const IST_OFFSET = "+05:30";

function parseYmd(ymd: string): [number, number, number] {
  const [y, m, d] = ymd.split("-").map(Number);
  return [y, m, d];
}

/** Today as yyyy-MM-dd in Asia/Kolkata */
export function getTodayYmdIST(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** Midnight IST for a calendar day as epoch ms */
function istMidnightMs(ymd: string): number {
  return new Date(`${ymd}T00:00:00${IST_OFFSET}`).getTime();
}

/** Weekday 0=Sun … 6=Sat for that instant in Asia/Kolkata */
function weekdaySun0IST(ms: number): number {
  const w = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    weekday: "short",
  }).format(new Date(ms));
  const map: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return map[w] ?? 0;
}

function addDaysYmd(ymd: string, deltaDays: number): string {
  const ms = istMidnightMs(ymd) + deltaDays * 86400000;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(ms));
}

/** Last calendar day of month `m` (1–12) */
function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Monday-start week (ISO-style) in IST */
function startOfWeekMondayYmdIST(todayYmd: string): string {
  const ms = istMidnightMs(todayYmd);
  const sun0 = weekdaySun0IST(ms);
  const daysFromMonday = sun0 === 0 ? 6 : sun0 - 1;
  return addDaysYmd(todayYmd, -daysFromMonday);
}

function endOfWeekSundayYmdIST(weekStartYmd: string): string {
  return addDaysYmd(weekStartYmd, 6);
}

export type DatePresetId =
  // Existing calendar-aligned ranges.
  | "today"
  | "this_week"
  | "this_month"
  | "this_year"
  // Single-day shifts — useful for "what came in yesterday" / "what's tomorrow's call list".
  | "yesterday"
  | "tomorrow"
  // Rolling windows (today inclusive). Differ from `this_week`/`this_month` which snap
  // to a calendar boundary — `last_7_days` and `last_30_days` are pure rolling ranges
  // every CRM user expects (HubSpot/Salesforce/Zoho all expose these).
  | "last_7_days"
  | "last_30_days"
  | "next_7_days"
  // Previous calendar month (e.g. on May 6 → Apr 1..Apr 30).
  | "last_month";

export function getPresetDateRangeIST(
  preset: DatePresetId,
  now: Date = new Date()
): { start: string; end: string } {
  const todayYmd = getTodayYmdIST(now);

  switch (preset) {
    case "today":
      return { start: todayYmd, end: todayYmd };
    case "yesterday": {
      const y = addDaysYmd(todayYmd, -1);
      return { start: y, end: y };
    }
    case "tomorrow": {
      const t = addDaysYmd(todayYmd, 1);
      return { start: t, end: t };
    }
    case "last_7_days":
      // 7-day window ending today (inclusive). 6 days back + today = 7 days total.
      return { start: addDaysYmd(todayYmd, -6), end: todayYmd };
    case "last_30_days":
      return { start: addDaysYmd(todayYmd, -29), end: todayYmd };
    case "next_7_days":
      return { start: todayYmd, end: addDaysYmd(todayYmd, 6) };
    case "this_week": {
      const start = startOfWeekMondayYmdIST(todayYmd);
      const end = endOfWeekSundayYmdIST(start);
      return { start, end };
    }
    case "this_month": {
      const [y, m] = parseYmd(todayYmd);
      const start = `${y}-${String(m).padStart(2, "0")}-01`;
      const last = daysInMonth(y, m);
      const end = `${y}-${String(m).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
      return { start, end };
    }
    case "last_month": {
      const [y, m] = parseYmd(todayYmd);
      // Walk back one month, handling the January wrap.
      const prevY = m === 1 ? y - 1 : y;
      const prevM = m === 1 ? 12 : m - 1;
      const start = `${prevY}-${String(prevM).padStart(2, "0")}-01`;
      const last = daysInMonth(prevY, prevM);
      const end = `${prevY}-${String(prevM).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
      return { start, end };
    }
    case "this_year": {
      const [y] = parseYmd(todayYmd);
      return { start: `${y}-01-01`, end: `${y}-12-31` };
    }
    default:
      return { start: todayYmd, end: todayYmd };
  }
}

/* ------------------------------------------------------------------ *
 * Follow-up presets
 *
 * The follow-up section has one range the generic preset table cannot
 * express: **Overdue** is OPEN-ENDED (`start` empty, `end` = yesterday).
 * Because `matchPreset` in `captureView` requires BOTH bounds, an Overdue
 * range used to be saved into a Favorite View as the literal date it
 * resolved to that day — so an "Overdue" view silently froze into
 * "follow-ups due on or before <the day I saved it>" and stopped being
 * overdue the next morning.
 *
 * These helpers are the single definition, shared by the filter drawer
 * (which paints the pills) and by view capture/hydrate (which must store
 * the TOKEN, never the resolved dates).
 * ------------------------------------------------------------------ */

/** Extra tokens the follow-up range understands on top of `DatePresetId`. */
export type FollowupPresetId = DatePresetId | "all" | "overdue";

/**
 * Marker stored for a follow-up range that is deliberately absolute but
 * open-ended (an end date with no start). Without it, hydrate could not tell
 * such a range apart from a legacy Overdue view saved before tokens existed.
 */
export const FOLLOWUP_ABSOLUTE_TOKEN = "absolute";

/** Yesterday as yyyy-MM-dd in Asia/Kolkata. */
export function getYesterdayYmdIST(now: Date = new Date()): string {
  return addDaysYmd(getTodayYmdIST(now), -1);
}

const FOLLOWUP_EXTRA_TOKENS = new Set(["all", "overdue", FOLLOWUP_ABSOLUTE_TOKEN]);

/**
 * Resolve a follow-up token to a range NOW.
 * `null` = "no range" — `all`, the absolute marker, or an unknown token
 * (never fall through to `getPresetDateRangeIST`'s today/today default,
 * which would invent a filter nobody asked for).
 */
export function getFollowupPresetRangeIST(
  preset: string,
  now: Date = new Date()
): { start: string; end: string } | null {
  if (preset === "overdue") return { start: "", end: getYesterdayYmdIST(now) };
  if (FOLLOWUP_EXTRA_TOKENS.has(preset)) return null;
  const range = getPresetDateRangeIST(preset as DatePresetId, now);
  // `getPresetDateRangeIST` defaults unknown ids to today/today; refuse that.
  return isDatePresetId(preset) ? range : null;
}

function isDatePresetId(value: string): value is DatePresetId {
  return [
    "today",
    "this_week",
    "this_month",
    "this_year",
    "yesterday",
    "tomorrow",
    "last_7_days",
    "last_30_days",
    "next_7_days",
    "last_month",
  ].includes(value);
}

/** True when `[start, end]` IS today's Overdue range (open start, ends yesterday). */
export function isOverdueFollowupRange(
  start: string | undefined,
  end: string | undefined,
  now: Date = new Date()
): boolean {
  const s = (start ?? "").trim();
  const e = (end ?? "").trim();
  return !s && !!e && e === getYesterdayYmdIST(now);
}
